import plusTealIcon from "@assets/images/plus-teal.svg";
import saveIcon from "@assets/images/save-icon.svg";
import { Button, IconButton, Paper } from "@material-ui/core";
import NavigateBeforeIcon from "@material-ui/icons/NavigateBefore";
import NavigateNextIcon from "@material-ui/icons/NavigateNext";
import DecimalFormat from "@saleor/components/DecimalFormat";
import MyTypography from "@saleor/components/MyTypography";
import { menuWidth, shrunkMenuWidth } from "@saleor/components/Siderbar";
import useAppState from "@saleor/hooks/useAppState";
import { makeStyles } from "@saleor/macaw-ui";
import {
  formatThousandDecimal,
  shortenLongDecimal
} from "@saleor/utils/number";
import classNames from "classnames";
import React from "react";
import InlineSVG from "react-inlinesvg";
import { FormattedMessage, useIntl } from "react-intl";

import { CreateOrderPageTab } from "../../CreateOrderView";

interface propsType {
  expandMenu?: boolean;
  bodyPaddingRight?: number;
}

const useStyles = makeStyles<propsType>(
  theme => ({
    menu: {
      zIndex: 100,
      padding: 0,
      position: "fixed",
      bottom: 0,
      right: 0,
      transition: "width 0.4s ease",
      width: "100%",
      [theme.breakpoints.up("md")]: {
        padding: "0 2.4rem",
        paddingRight: props => `calc(2.4rem + ${props.bodyPaddingRight}px)`,
        width: props =>
          props.expandMenu
            ? `calc(100% - ${menuWidth}px)`
            : `calc(100% - ${shrunkMenuWidth}px)`
      }
    },
    wrapper: {
      display: "block",
      width: "100%",
      alignItems: "center",
      boxShadow: "0px -8px 24px rgb(0 0 0 / 8%)",
      borderRadius: "20px 20px 0px 0px"
    },
    section: {
      display: "flex",
      padding: "1.6rem 2.4rem",
      justifyContent: "space-between",
      gap: "0 2.4rem"
    },
    divider: {
      margin: 0,
      border: 0,
      borderTop: `1px dashed ${theme.palette.grey[500]}`
    },
    summaryCaption: {
      color: theme.palette.grey[900]
    },
    number: {
      fontSize: "1.8rem",
      lineHeight: "2.4rem"
    },
    sectionRight: {
      display: "flex"
    },
    price: {
      minWidth: 152,
      marginRight: "2.4rem",
      "&:last-child": {
        marginRight: 0,
        minWidth: "75px"
      }
    },
    priceCaption: {
      color: theme.palette.grey[600],
      marginBottom: "0.6rem"
    },
    netPriceNumber: {
      color: theme.palette.info.dark
    },
    btnAddProduct: {
      alignItems: "center",
      height: 48,
      fontSize: "1.6rem",
      padding: "0 13px",
      "& .MuiButton-label": {
        fontWeight: 700,
        gap: "0 8px"
      }
    },
    btnConfirmOrder: {
      color: "#ffffff",
      alignItems: "center",
      fontSize: "1.8rem",
      lineHeight: "2.8rem",
      height: 48,
      padding: "0 73px 0 74px",
      "& .MuiButton-label": {
        fontWeight: 700
      }
    },
    leftContainer: {
      display: "flex",
      alignItems: "center",
      "& button": {
        marginRight: "1.6rem"
      }
    },
    pagination: {
      display: "flex",
      justifyContent: "flex-end",
      color: theme.palette.grey[900],
      "& >div": {
        fontSize: 12,
        lineHeight: "18px"
      },
      "& .MuiSelect-root": {
        color: theme.palette.grey[900]
      },
      "& .MuiButtonBase-root": {
        borderRadius: "6px",
        width: "2.4rem",
        height: "2.4rem",
        boxShadow:
          "0px 1px 1px rgba(0, 0, 0, 0.1), 0px 0px 0px 1px rgba(0, 0, 0, 0.12)",
        border: "none",
        "&:hover": {
          boxShadow:
            "0px 1px 1px rgba(0, 0, 0, 0.1), 0px 0px 0px 1px rgba(0, 0, 0, 0.24)"
        },
        "&:active": {
          boxShadow:
            "0px 1px 1px rgba(0, 0, 0, 0.1), 0px 0px 0px 1px rgba(0, 0, 0, 0.24), 0px 0px 0px 3px rgba(1, 178, 237, 0.36)"
        },
        "& svg": {
          fontSize: "16px"
        }
      },
      "& .MuiButtonBase-root.Mui-disabled": {
        background: theme.palette.background.default,
        boxShadow: "0px 0px 0px 1px rgba(0, 0, 0, 0.08)",
        "& svg": {
          color: theme.palette.grey[500]
        }
      }
    },
    iconButton: {
      "& > span:first-of-type": {
        transition: "none"
      },
      padding: "0.4rem",
      "&:hover": {
        color: theme.palette.grey[900]
      }
    },
    buttonApply: {
      padding: 12,
      width: 180,
      height: 48,
      fontSize: 16,
      lineHeight: "24px",
      "& .MuiButton-label": {
        fontWeight: 700
      }
    }
  }),
  { name: "CreateOrderFooter" }
);

interface CreateOrderFooterProps {
  onOrder: () => void;
  onPopup?: () => void;
  onNextTab?: (activeTab) => void;
  onPreviousTab?: (activeTab) => void;
  activeTab: CreateOrderPageTab;
  disabled?: boolean;
  disabledUpdate?: boolean;
  disabledAddMore?: boolean;
  hasValidationError?: boolean;
  priceData: {
    itemCount: number;
    totalPrice: number;
    vat: number;
    netPrice: number;
    usd: string;
  };
  onApplyAll: () => void;
  onSaveDraft: () => void;
  taxPercent: number;
}

const CreateOrderFooter: React.FC<CreateOrderFooterProps> = ({
  onOrder,
  onPopup,
  onNextTab,
  onPreviousTab,
  activeTab,
  disabled,
  disabledUpdate,
  hasValidationError,
  priceData,
  disabledAddMore,
  onSaveDraft,
  taxPercent
}) => {
  const [appState, _] = useAppState();
  const classes = useStyles({
    expandMenu: appState.expandMenu,
    bodyPaddingRight: appState.bodyPaddingRight
  });
  const intl = useIntl();
  const { itemCount, totalPrice, vat, netPrice } = priceData;
  const handleNextTab = () => {
    switch (activeTab) {
      case CreateOrderPageTab.agency:
        onNextTab(CreateOrderPageTab.orderHeader);
        break;
      case CreateOrderPageTab.orderHeader:
        onNextTab(CreateOrderPageTab.materialItem);
        break;
      default:
        break;
    }
  };
  const handlePrevTab = () => {
    switch (activeTab) {
      case CreateOrderPageTab.orderHeader:
        onPreviousTab(CreateOrderPageTab.agency);
        break;
      case CreateOrderPageTab.materialItem:
        onPreviousTab(CreateOrderPageTab.orderHeader);
        break;
      default:
        break;
    }
  };
  return (
    <div className={classes.menu}>
      <Paper className={classes.wrapper}>
        <div className={classes.section}>
          <div>
            <MyTypography
              variant="subtitle2"
              className={classes.summaryCaption}
            >
              {intl.formatMessage({
                defaultMessage: "Order Summary"
              })}
            </MyTypography>
            <MyTypography variant="body1" className={classes.number}>
              ({itemCount}{" "}
              {itemCount >= 2
                ? intl.formatMessage({ defaultMessage: "items" })
                : intl.formatMessage({ defaultMessage: "item" })}
              )
            </MyTypography>
          </div>
          <div className={classes.sectionRight}>
            <div className={classes.price}>
              <MyTypography variant="caption" className={classes.priceCaption}>
                {intl.formatMessage({
                  defaultMessage: "ยอดรวมสุทธิ"
                })}
              </MyTypography>
              <MyTypography variant="body1" className={classes.number}>
                {netPrice ? (
                  <DecimalFormat
                    value={netPrice}
                    isGrayDecimal={false}
                    countDecimal={2}
                  />
                ) : (
                  "0.00"
                )}
              </MyTypography>
            </div>
            <div className={classes.price}>
              <MyTypography variant="caption" className={classes.priceCaption}>
                {`ภาษีมูลค่าเพิ่ม ${shortenLongDecimal(taxPercent * 100)}%`}
              </MyTypography>
              <MyTypography variant="body1" className={classes.number}>
                {vat ? (
                  <DecimalFormat
                    value={vat}
                    isGrayDecimal={false}
                    countDecimal={2}
                  />
                ) : (
                  "0.00"
                )}
              </MyTypography>
            </div>
            <div className={classes.price}>
              <MyTypography variant="caption" className={classes.priceCaption}>
                {intl.formatMessage({
                  defaultMessage: "ยอดรวมภาษีมูลค่าเพิ่ม"
                })}
              </MyTypography>
              <MyTypography
                variant="subtitle1"
                className={classes.netPriceNumber}
              >
                <span className={classes.totalPrice}>
                  {totalPrice ? formatThousandDecimal(totalPrice, 2) : "0.00"}
                </span>
              </MyTypography>
            </div>
            <div className={classes.price}>
              <MyTypography variant="caption" className={classes.priceCaption}>
                {intl.formatMessage({
                  defaultMessage: "Currency"
                })}
              </MyTypography>
              <MyTypography variant="body1" className={classes.number}>
                {priceData.usd}
              </MyTypography>
            </div>
          </div>
        </div>
        <hr className={classes.divider} />
        <div className={classes.section}>
          <div className={classes.leftContainer}>
            <div className={classes.pagination}>
              <IconButton
                className={classNames(classes.iconButton)}
                onClick={handlePrevTab}
                disabled={activeTab === CreateOrderPageTab.agency}
                name="prev-tab-button"
                id="prev-tab-button"
              >
                <NavigateBeforeIcon />
              </IconButton>
              <IconButton
                className={classNames(classes.iconButton)}
                onClick={handleNextTab}
                disabled={activeTab === CreateOrderPageTab.materialItem}
                name="next-tab-button"
                id="next-tab-button"
              >
                <NavigateNextIcon />
              </IconButton>
            </div>
            <Button
              variant="outlined"
              color="secondary"
              onClick={onSaveDraft}
              className={classes.btnAddProduct}
              startIcon={<InlineSVG src={saveIcon} />}
              disabled={disabled}
              id="save-draft-button"
              name="save-draft-button"
            >
              <span>
                <FormattedMessage defaultMessage="บันทึกฉบับร่าง" />
              </span>
            </Button>
            {activeTab === CreateOrderPageTab.materialItem ? (
              <Button
                variant="outlined"
                color="secondary"
                onClick={onPopup}
                className={classes.btnAddProduct}
                disabled={disabledAddMore}
                startIcon={<InlineSVG src={plusTealIcon} />}
                name="add-item-button"
                id="add-item-button"
              >
                <span>
                  <FormattedMessage defaultMessage="เพิ่มรายการสินค้า" />
                </span>
              </Button>
            ) : null}
            {/* {activeTab === CreateOrderPageTab.materialItem ? (*/}
            {/*  <Button*/}
            {/*    variant="outlined"*/}
            {/*    color="secondary"*/}
            {/*    className={classes.btnAddProduct}*/}
            {/*  >*/}
            {/*    <span>*/}
            {/*      <FormattedMessage defaultMessage="ATP/CTP" />*/}
            {/*    </span>*/}
            {/*  </Button>*/}
            {/* ) : null}*/}

            {
              // activeTab === CreateOrderPageTab.orderHeader ? (
              //   <Button
              //     variant="contained"
              //     className={classNames(classes.buttonApply, "defaultB")}
              //     onClick={onApplyAll}
              //     disabled={disabled}
              //   >
              //     {intl.formatMessage({
              //       defaultMessage: "APPLY ALL",
              //       description: "button label"
              //     })}
              //   </Button>
              // ) : null
            }
          </div>
          <Button
            variant="contained"
            color="secondary"
            onClick={onOrder}
            disabled={hasValidationError || disabled || disabledUpdate}
            className={classes.btnConfirmOrder}
            name="confirm-order-button"
            id="confirm-order-button"
          >
            <FormattedMessage defaultMessage="ตกลงสั่งซื้อ" />
          </Button>
        </div>
      </Paper>
    </div>
  );
};

export default CreateOrderFooter;
